<template>
    <div class="action-message-items">
        <div class="relative" v-click-outside="closeNotificationsDropdown">
            <a
                class="block relative text-lg hover:opacity-40 outline-none rounded"
                href="#"
                role="button"
                @click.prevent="toggleOpen()"
                aria-haspopup="true"
                :aria-expanded="notificationsOpen"
            >
                <i
                    class="fas fa-bell"
                    :class="{ 'ring-bell': ringBell }"
                    v-if="hasUnread"
                ></i>
                <i class="fal fa-bell" v-if="!hasUnread"></i>
                <span
                    v-if="hasUnread"
                    class="rounded-full h-2 w-2 flex items-center justify-center absolute -top-1 -right-1 z-10 text-white bg-red-500"
                ></span>
            </a>

            <div
                class="notifications-popover absolute sm:absolute flex flex-col w-[408px] pb-2 items-start right-0 top-[45px] rounded-lg border border-slate-200 bg-white dark:bg-slate-800 shadow-sm z-10 max-md:fixed max-md:inset-0 max-md:w-full max-md:h-full max-md:rounded-none max-md:border-0 max-md:z-[9999]"
                v-show="notificationsOpen && !isRedirecting"
                v-cloak
            >
                <div
                    class="flex flex-col w-full h-full flex-1 min-h-0 max-md:overflow-y-auto"
                >
                    <div
                        class="flex p-4 pb-2 items-start self-stretch justify-between"
                    >
                        <h3
                            class="text-slate-800 dark:text-slate-100 font-inter text-base font-medium leading-6"
                        >
                            Notifications
                        </h3>
                        <button
                            @click="closeNotificationsDropdown"
                            class="hidden max-md:flex p-1 items-center justify-center text-slate-400 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                    </div>

                    <div
                        class="flex flex-col w-full max-h-[400px] overflow-y-auto max-md:max-h-none max-md:overflow-y-auto"
                    >
                        <div
                            v-if="notifications.length === 0"
                            class="flex flex-col items-center justify-center py-10 px-4"
                        >
                            <svg
                                width="40"
                                height="40"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                class="text-slate-300 dark:text-slate-600 mb-3"
                            >
                                <path
                                    d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9ZM13.73 21a2 2 0 0 1-3.46 0"
                                    stroke="currentColor"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            <p class="text-slate-400 font-inter text-sm">
                                No notifications yet
                            </p>
                        </div>

                        <div
                            v-for="(notification, $index) in notifications"
                            :key="'key-' + $index"
                            class="flex p-4 items-start gap-3 self-stretch border-b border-slate-100 dark:border-slate-700 last:border-b-0 max-md:border-slate-200 dark:max-md:border-slate-700 transition-colors"
                            :class="{ 'bg-blue-50/40 dark:bg-blue-900/20': !notification.read_at }"
                        >
                            <div
                                class="flex p-2.5 items-center rounded-full relative shrink-0"
                                :class="
                                    notification.read_at
                                        ? 'bg-slate-50 dark:bg-slate-700/40 text-slate-600 dark:text-slate-400'
                                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                "
                            >
                                <span
                                    v-if="!notification.read_at"
                                    class="rounded-full h-2 w-2 flex items-center justify-center absolute -top-1 -right-1 z-10 text-white bg-red-500"
                                ></span>
                                <!-- Email forward icon for redemption imports -->
                                <svg
                                    v-if="isRedemptionImport(notification)"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M16.667 9.16732V6.83415C16.667 6.6948 16.6659 6.5696 16.6654 6.45655L11.5832 10.6151C10.6623 11.3686 9.3384 11.3686 8.41748 10.6151L3.33448 6.45655C3.33393 6.5696 3.33366 6.6948 3.33366 6.83415V13.1672C3.33366 13.6476 3.33472 13.9581 3.35401 14.1942C3.3725 14.4205 3.40382 14.5045 3.42481 14.5457C3.50468 14.7024 3.63194 14.8296 3.78858 14.9095C3.82978 14.9305 3.91382 14.9618 4.14014 14.9803C4.37625 14.9996 4.6867 15.0007 5.16716 15.0007H6.25033C6.71057 15.0007 7.08366 15.3738 7.08366 15.834C7.08366 16.2942 6.71057 16.6673 6.25033 16.6673H5.16716C4.7142 16.6673 4.32396 16.6682 4.00424 16.6421C3.67485 16.6152 3.34699 16.5553 3.03174 16.3947C2.56156 16.1551 2.17927 15.7728 1.93962 15.3026C1.77899 14.9873 1.71915 14.6595 1.69222 14.3301C1.6661 14.0104 1.667 13.6201 1.667 13.1672V6.83415C1.667 6.38119 1.6661 5.99095 1.69222 5.67123C1.71915 5.34185 1.77899 5.01398 1.93962 4.69873C2.17926 4.22855 2.56156 3.84626 3.03174 3.60661C3.34699 3.44599 3.67485 3.38614 4.00424 3.35922C4.32396 3.33309 4.7142 3.33399 5.16716 3.33399H14.8335C15.2865 3.33399 15.6767 3.33309 15.9964 3.35922C16.3258 3.38614 16.6537 3.44599 16.9689 3.60661C17.4391 3.84626 17.8214 4.22855 18.061 4.69873C18.2217 5.01398 18.2815 5.34185 18.3084 5.67123C18.3346 5.99095 18.3337 6.38119 18.3337 6.83415V9.16732C18.3337 9.62756 17.9606 10.0007 17.5003 10.0007C17.0401 10.0007 16.667 9.62756 16.667 9.16732ZM5.16716 5.00065C4.73005 5.00065 4.43375 5.00231 4.20606 5.01693L9.47299 9.3252C9.77994 9.5763 10.2207 9.5763 10.5277 9.3252L15.7938 5.01693C15.5662 5.00236 15.2701 5.00065 14.8335 5.00065H5.16716Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        d="M16.4229 18.0898C16.0974 18.4153 15.5699 18.4153 15.2445 18.0898C14.919 17.7644 14.919 17.2369 15.2445 16.9115L16.3219 15.834H12.5003C12.0401 15.834 11.667 16.2071 11.667 16.6673V17.5007C11.667 17.9609 11.2939 18.334 10.8337 18.334C10.3734 18.334 10.0003 17.9609 10.0003 17.5007V16.6673C10.0003 15.2866 11.1196 14.1673 12.5003 14.1673H16.3219L15.2445 13.0898C14.919 12.7644 14.919 12.2369 15.2445 11.9115C15.5699 11.586 16.0974 11.586 16.4229 11.9115L18.9229 14.4115C19.2483 14.7369 19.2483 15.2644 18.9229 15.5898L16.4229 18.0898Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <!-- Default clipboard icon -->
                                <svg
                                    v-else
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clip-path="url(#clip0_2277_31052)">
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M2.50033 5.41667C2.50033 3.80584 3.80616 2.5 5.41699 2.5H12.1572C13.2672 2.5 14.167 3.39982 14.167 4.5098V6.66667H15.0003C16.381 6.66667 17.5003 7.78595 17.5003 9.16667V15C17.5003 16.3807 16.381 17.5 15.0003 17.5H12.917C12.4568 17.5 12.0837 17.1269 12.0837 16.6667C12.0837 16.2064 12.4568 15.8333 12.917 15.8333H15.0003C15.4606 15.8333 15.8337 15.4602 15.8337 15V9.16667C15.8337 8.70643 15.4606 8.33333 15.0003 8.33333H5.41699C4.96966 8.33333 4.54585 8.23263 4.16699 8.05265V9.58333C4.16699 10.0436 3.7939 10.4167 3.33366 10.4167C2.87342 10.4167 2.50033 10.0436 2.50033 9.58333V5.41667ZM4.16699 5.41667C4.16699 6.10702 4.72664 6.66667 5.41699 6.66667H12.5003V4.5098C12.5003 4.32029 12.3467 4.16667 12.1572 4.16667H5.41699C4.72664 4.16667 4.16699 4.72631 4.16699 5.41667Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            d="M13.3337 11.25C13.3337 11.4801 13.1471 11.6667 12.917 11.6667C12.6869 11.6667 12.5003 11.4801 12.5003 11.25C12.5003 11.0199 12.6869 10.8333 12.917 10.8333C13.1471 10.8333 13.3337 11.0199 13.3337 11.25Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M13.9587 11.25C13.9587 11.8253 13.4923 12.2917 12.917 12.2917C12.3417 12.2917 11.8753 11.8253 11.8753 11.25C11.8753 10.6747 12.3417 10.2083 12.917 10.2083C13.4923 10.2083 13.9587 10.6747 13.9587 11.25ZM12.917 11.6667C13.1471 11.6667 13.3337 11.4801 13.3337 11.25C13.3337 11.0199 13.1471 10.8333 12.917 10.8333C12.6869 10.8333 12.5003 11.0199 12.5003 11.25C12.5003 11.4801 12.6869 11.6667 12.917 11.6667Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M1.66699 15C1.66699 13.1591 3.15938 11.6667 5.00033 11.6667C5.46056 11.6667 5.83366 12.0398 5.83366 12.5C5.83366 12.9602 5.46056 13.3333 5.00033 13.3333C4.07985 13.3333 3.33366 14.0795 3.33366 15C3.33366 15.9205 4.07985 16.6667 5.00033 16.6667C5.46056 16.6667 5.83366 17.0398 5.83366 17.5C5.83366 17.9602 5.46056 18.3333 5.00033 18.3333C3.15938 18.3333 1.66699 16.841 1.66699 15ZM6.66699 12.5C6.66699 12.0398 7.04009 11.6667 7.50033 11.6667C9.34127 11.6667 10.8337 13.1591 10.8337 15C10.8337 16.841 9.34127 18.3333 7.50033 18.3333C7.04009 18.3333 6.66699 17.9602 6.66699 17.5C6.66699 17.0398 7.04009 16.6667 7.50033 16.6667C8.4208 16.6667 9.16699 15.9205 9.16699 15C9.16699 14.0795 8.4208 13.3333 7.50033 13.3333C7.04009 13.3333 6.66699 12.9602 6.66699 12.5ZM5.00033 15C5.00033 14.5398 5.37342 14.1667 5.83366 14.1667H6.66699C7.12723 14.1667 7.50033 14.5398 7.50033 15C7.50033 15.4602 7.12723 15.8333 6.66699 15.8333H5.83366C5.37342 15.8333 5.00033 15.4602 5.00033 15Z"
                                            fill="currentColor"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2277_31052">
                                            <rect
                                                width="20"
                                                height="20"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>

                            <div
                                class="flex flex-col items-start gap-2 flex-1 min-w-0"
                            >
                                <div
                                    v-if="isRedemptionImport(notification)"
                                    class="flex flex-col items-start self-stretch"
                                >
                                    <div
                                        class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5"
                                    >
                                        {{ notification.title }}
                                    </div>

                                    <div
                                        class="text-slate-500 font-inter text-sm font-normal leading-5 mt-1"
                                    >
                                        {{
                                            getTimeAgo(
                                                notification.created_at ||
                                                    new Date()
                                            )
                                        }}
                                    </div>

                                    <div class="flex pt-2 items-center gap-3">
                                        <button
                                            @click="
                                                handleCtaNavigate(
                                                    notification,
                                                    notification.route ||
                                                        '/trips/unfiled'
                                                )
                                            "
                                            class="flex px-4 py-2 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 transition-all hover:bg-slate-50 dark:hover:bg-slate-700"
                                        >
                                            {{
                                                notification.route &&
                                                notification.route !==
                                                    '/trips/unfiled'
                                                    ? 'View redemption'
                                                    : 'View unfiled redemptions'
                                            }}
                                        </button>
                                    </div>
                                </div>

                                <div
                                    v-else-if="isBonusAlert(notification)"
                                    class="flex flex-col items-start self-stretch"
                                >
                                    <div
                                        class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 cursor-pointer"
                                        @click="
                                            redirectToBonusAlert(notification)
                                        "
                                    >
                                        {{ getBonusAlertTitle(notification) }}
                                    </div>

                                    <div
                                        class="self-stretch text-slate-600 dark:text-slate-300 font-inter text-sm font-normal leading-5 mt-1"
                                    >
                                        {{ getBonusAlertMessage(notification) }}
                                    </div>

                                    <div
                                        class="self-stretch text-slate-500 font-inter text-sm font-normal leading-5 mt-1"
                                    >
                                        {{
                                            getTimeAgo(
                                                notification.created_at ||
                                                    new Date()
                                            )
                                        }}
                                    </div>

                                    <div class="flex pt-2 items-center gap-3">
                                        <button
                                            @click="
                                                redirectToBonusAlert(
                                                    notification
                                                )
                                            "
                                            class="flex px-4 py-2 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 transition-all hover:bg-slate-50 dark:hover:bg-slate-700"
                                        >
                                            View details
                                        </button>
                                    </div>
                                </div>

                                <div
                                    v-else-if="
                                        getSenderNameFromNotification(
                                            notification
                                        ) &&
                                        getSenderEmailFromNotification(
                                            notification
                                        )
                                    "
                                    class="flex flex-col items-start self-stretch"
                                >
                                    <div
                                        class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 cursor-pointer"
                                        @click="
                                            redirectToInvitations(notification)
                                        "
                                    >
                                        {{
                                            getSenderNameFromNotification(
                                                notification
                                            )
                                        }}
                                        ({{
                                            getSenderEmailFromNotification(
                                                notification
                                            )
                                        }}) invites you to share your wallet.
                                    </div>

                                    <div
                                        class="self-stretch text-slate-500 font-inter text-sm font-normal leading-5 mt-1"
                                    >
                                        {{
                                            getTimeAgo(
                                                notification.created_at ||
                                                    new Date()
                                            )
                                        }}
                                    </div>

                                    <div class="flex pt-2 items-center gap-3">
                                        <button
                                            @click="
                                                acceptSingleInvitation(
                                                    notification
                                                )
                                            "
                                            class="flex px-4 py-2 items-start gap-2 rounded border border-slate-200 bg-white dark:bg-slate-800 shadow-sm text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 transition-all"
                                        >
                                            <svg
                                                width="14"
                                                height="14"
                                                class="sm:w-4 sm:h-4"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M13.3337 4L6.00033 11.3333L2.66699 8"
                                                    stroke="currentColor"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                            Accept
                                        </button>

                                        <button
                                            @click="
                                                rejectInvitation(notification)
                                            "
                                            class="flex px-4 py-2 items-start gap-2 rounded border border-slate-200 bg-white dark:bg-slate-800 shadow-sm text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 transition-all"
                                        >
                                            <svg
                                                width="14"
                                                height="14"
                                                class="sm:w-4 sm:h-4"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M12 4L4 12M4 4L12 12"
                                                    stroke="currentColor"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                            Reject
                                        </button>
                                    </div>
                                </div>

                                <div
                                    v-else-if="isConnectionIssue(notification)"
                                    class="flex flex-col items-start self-stretch"
                                >
                                    <div class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5">
                                        {{ notification.title || 'Notification' }}
                                    </div>

                                    <div
                                        class="self-stretch text-slate-400 font-inter text-xs font-normal leading-4 mt-1.5"
                                    >
                                        {{
                                            getTimeAgo(
                                                notification.created_at ||
                                                    new Date()
                                            )
                                        }}
                                    </div>

                                    <ConnectionResolver
                                        :notification="notification"
                                        @resolved="markNotificationRead(notification)"
                                    />
                                </div>

                                <div
                                    v-else
                                    class="flex flex-col items-start self-stretch"
                                >
                                    <div
                                        class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5"
                                        :class="{
                                            'cursor-pointer hover:text-slate-600':
                                                notification.route,
                                        }"
                                        @click="
                                            notification.route
                                                ? handleCtaNavigate(
                                                      notification,
                                                      notification.route
                                                  )
                                                : null
                                        "
                                    >
                                        {{
                                            notification.title || 'Notification'
                                        }}
                                    </div>

                                    <div
                                        v-if="notification.message"
                                        class="self-stretch text-slate-500 font-inter text-sm font-normal leading-5 mt-1 line-clamp-2"
                                    >
                                        {{ notification.message }}
                                    </div>

                                    <div
                                        class="self-stretch text-slate-400 font-inter text-xs font-normal leading-4 mt-1.5"
                                    >
                                        {{
                                            getTimeAgo(
                                                notification.created_at ||
                                                    new Date()
                                            )
                                        }}
                                    </div>

                                    <div
                                        v-if="notification.route"
                                        class="flex pt-2 items-center gap-3"
                                    >
                                        <button
                                            @click="
                                                handleCtaNavigate(
                                                    notification,
                                                    notification.route
                                                )
                                            "
                                            class="inline-flex items-center gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 shadow-sm dark:shadow-slate-900/50 text-slate-700 dark:text-slate-200 font-inter text-xs font-medium leading-4 transition-all hover:bg-slate-50 dark:hover:bg-slate-700"
                                        >
                                            {{ notification.cta_label || 'View details' }}
                                            <i class="far fa-arrow-right text-[10px]"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="flex px-2 pb-2 items-center border-t border-slate-200 dark:border-slate-700 pt-2"
                    >
                        <a
                            href="/notifications"
                            @click.prevent="goToAllNotifications"
                            class="flex px-2 py-1.5 items-center justify-center gap-2 flex-1 rounded bg-transparent no-underline hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                        >
                            View all notifications
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-800/50"
        v-if="showRejectModal"
    >
        <div
            class="w-[95%] sm:w-[600px] flex flex-col items-start p-4 sm:p-6 gap-4 sm:gap-6 rounded-md bg-white dark:bg-slate-800 shadow-md dark:shadow-slate-900/50"
        >
            <div class="flex justify-between items-center w-full">
                <div class="flex items-center gap-3">
                    <div
                        class="flex w-10 h-10 sm:w-12 sm:h-12 p-3 sm:p-3.5 flex-col justify-center items-center rounded-full bg-red-50"
                    >
                        <svg
                            width="20"
                            height="20"
                            class="sm:w-6 sm:h-6"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M12.8592 4.49407C12.4751 3.83531 11.5239 3.83531 11.1398 4.49408L4.13818 16.5017C3.75059 17.1665 4.23028 18 4.99786 18H19.0011C19.7687 18 20.2484 17.1665 19.8608 16.5017L12.8592 4.49407ZM9.41208 3.48664C10.5679 1.50446 13.4311 1.50445 14.5869 3.48664L21.5885 15.4943C22.7529 17.4911 21.3135 20 19.0011 20H4.99786C2.68545 20 1.24611 17.4911 2.41045 15.4943L9.41208 3.48664ZM11.9995 8.01917C12.5518 8.01917 12.9995 8.46689 12.9995 9.01917V12.0134C12.9995 12.5657 12.5518 13.0134 11.9995 13.0134C11.4472 13.0134 10.9995 12.5657 10.9995 12.0134V9.01917C10.9995 8.46689 11.4472 8.01917 11.9995 8.01917ZM10.7495 15C10.7495 14.3096 11.3091 13.75 11.9995 13.75C12.6899 13.75 13.2495 14.3096 13.2495 15C13.2495 15.6904 12.6899 16.25 11.9995 16.25C11.3091 16.25 10.7495 15.6904 10.7495 15Z"
                                fill="#EF4444"
                            />
                        </svg>
                    </div>
                    <h5
                        class="text-slate-800 dark:text-slate-100 font-inter text-base sm:text-lg font-medium"
                    >
                        Reject invitation
                    </h5>
                </div>
                <button
                    type="button"
                    class="z-10 w-9 h-9 sm:w-10 sm:h-10 rounded flex justify-center items-center focus:outline-none focus:ring focus:ring-inset focus:ring-slate-200"
                    @click="closeRejectModal"
                >
                    <svg
                        width="18"
                        height="18"
                        class="sm:w-5 sm:h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="#64748B"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
            </div>

            <p class="text-slate-500 dark:text-slate-400 font-inter text-sm font-normal leading-5">
                You are about to reject
                {{ getSenderNameFromNotification(selectedNotification) }}'s
                invitation to share your wallet with them.
            </p>

            <div class="flex mt-4 self-end gap-3">
                <button
                    type="button"
                    class="flex py-2 px-4 justify-center items-center gap-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-200 font-medium shadow-sm"
                    @click="closeRejectModal"
                >
                    Cancel
                </button>

                <button
                    type="button"
                    class="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-red-500 text-sm text-white font-medium shadow-sm hover:bg-red-600 transition-colors duration-150"
                    :class="{ 'opacity-50 pointer-events-none': loading }"
                    @click="confirmRejectInvitation"
                >
                    <svg
                        v-if="loading"
                        class="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    {{ loading ? 'Rejecting...' : 'Reject invitation' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import { notify } from 'notiwind';
import { usePage, router } from '@inertiajs/vue3';
import { useEcho } from '@laravel/echo-vue';
import ConnectionResolver from '@/components/notifications/connection-resolver.vue';
import { useDecisionsModalStore } from '@/store/decisions-modal';

const decisionsModalStore = useDecisionsModalStore();

const notificationsOpen = ref(false);
const notifications = ref([]);
const ringBell = ref(true);
const showRejectModal = ref(false);
const selectedNotification = ref(null);
const loading = ref(false);
const isRedirecting = ref(false);

const pageUserInvitations = computed(() => {
    const page = usePage();
    return page.props?.user_invitations || [];
});

watch(
    pageUserInvitations,
    () => {
        getNotifications();
    },
    { deep: true }
);

const hasUnread = computed(() => {
    return notifications.value.some((item) => !item.read_at);
});

const toggleOpen = () => {
    notificationsOpen.value = !notificationsOpen.value;
};

const getNotifications = async () => {
    try {
        const response = await ApiService.apiCall('/notifications', 'get');
        let notificationsFromAPI = response.data.notifications || [];

        notificationsFromAPI = notificationsFromAPI.filter((notif) => {
            // Only filter out empty messages
            if (!notif.message || notif.message.trim() === '') {
                // Allow redemption import notifications through (they use title, not message)
                if (
                    notif.notification_type === 'redemption_import' ||
                    notif.notification_type === 'redemption_import_updated'
                ) {
                    return true;
                }
                return false;
            }

            // Filter out the specific unknown user invitation message
            if (
                notif.message ===
                'Unknown User (unknown@example.com) invites you to share your wallet with them.'
            ) {
                return false;
            }

            return true;
        });

        notifications.value = notificationsFromAPI;

        const page = usePage();
        const userInvitations = page.props.user_invitations || [];

        if (userInvitations.length > 0) {
            const pendingInvitations = userInvitations.filter(
                (invite) => invite.status === 'N'
            );

            pendingInvitations.forEach((invite) => {
                if (
                    invite.wallet &&
                    invite.wallet.users &&
                    invite.wallet.users.length > 0 &&
                    invite.wallet.users[0].name &&
                    invite.wallet.users[0].email
                ) {
                    const walletUser = invite.wallet.users[0];

                    const alreadyExists = notifications.value.some(
                        (n) => n.walletUserEmail === walletUser.email
                    );

                    if (!alreadyExists) {
                        notifications.value.push({
                            ...invite,
                            route: '/settings/invitations',
                            message: `${walletUser.name} (${walletUser.email}) has invited you to share their wallet`,
                            read_at: null,
                            created_at: invite.created_at,
                            walletUserEmail: walletUser.email,
                        });
                    }
                }
            });
        }

        if (hasUnread.value) {
            ringThatBell();
        }
    } catch (error) {
        console.error('Error fetching notifications:', error);
    }
};

const markAllRead = () => {
    ApiService.apiCall('/notifications/mark-all-read', 'post')
        .then(() => {
            notifications.value.forEach((item) => {
                item.read_at = new Date().toISOString().slice(0, 10);
            });
        })
        .catch((response) => {});
};

const markNotificationRead = (notification) => {
    if (!notification || !notification.id || notification.read_at) {
        return;
    }

    notification.read_at = new Date().toISOString();

    ApiService.apiCall(
        `/notifications/${notification.id}/mark-read`,
        'post'
    ).catch(() => {
        notification.read_at = null;
    });
};

const closeNotificationsDropdown = () => {
    notificationsOpen.value = false;
};

const ringThatBell = () => {
    ringBell.value = false;
    setTimeout(() => {
        ringBell.value = true;
    }, 1000);
};

const getSenderEmailFromNotification = (notification) => {
    if (!notification) return null;

    if (
        notification.wallet?.users &&
        notification.wallet.users.length > 0 &&
        notification.wallet.users[0].email
    )
        return notification.wallet.users[0].email;
    if (notification.wallet?.user?.email) return notification.wallet.user.email;
    if (notification.sender_email) return notification.sender_email;
    if (notification.user?.email) return notification.user.email;
    if (notification.email) return notification.email;
    if (notification.from_email) return notification.from_email;

    return null;
};

const getSenderNameFromNotification = (notification) => {
    if (!notification) return null;

    if (
        notification.wallet?.users &&
        notification.wallet.users.length > 0 &&
        notification.wallet.users[0].name
    )
        return notification.wallet.users[0].name;
    if (notification.wallet?.user?.name) return notification.wallet.user.name;
    if (notification.sender_name) return notification.sender_name;
    if (notification.user?.name) return notification.user.name;
    if (notification.sender) return notification.sender;
    if (notification.from_user) return notification.from_user;

    return null;
};

const isRedemptionImport = (notification) => {
    return (
        notification?.notification_type === 'redemption_import' ||
        notification?.notification_type === 'redemption_import_updated'
    );
};

const isBonusAlert = (notification) => {
    return (
        notification?.type ===
        'App\\Notifications\\TransferRateBonusNotification'
    );
};

const isConnectionIssue = (notification) => {
    return (
        notification?.category === 'account' &&
        !!notification?.member_guid
    );
};

const getBonusAlertTitle = (notification) => {
    return notification?.title || 'New Bonus Alert';
};

const getBonusAlertMessage = (notification) => {
    return notification?.message || 'Check out the latest bonus offer.';
};

const getBonusAlertRoute = (notification) => {
    return notification?.route || '/resources/transfer-partners-guide';
};

const redirectToBonusAlert = (notification) => {
    const route = getBonusAlertRoute(notification);
    if (route) {
        markNotificationRead(notification);
        isRedirecting.value = true;
        window.location.href = route;
    }
};

const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return `${seconds} sec ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;

    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const acceptSingleInvitation = (notification) => {
    if (!notification || !notification.guid) {
        console.error('No valid invitation data provided');
        return;
    }

    const guid = notification.guid;
    notificationsOpen.value = false;

    router.post(
        `/accept-wallet-invite/${guid}`,
        { user_exists: 'true' },
        {
            onSuccess: (page) => {
                const message =
                    page.props.success || 'Invitation accepted successfully.';
                notify(
                    {
                        group: 'success',
                        title: 'Success',
                        text: message,
                    },
                    4000
                );
            },
            onError: (errors) => {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Failed to accept invitation.',
                    },
                    4000
                );
            },
        }
    );
};
const rejectInvitation = (notification) => {
    if (!notification || !notification.guid) {
        console.error('No valid invitation data provided');
        return;
    }

    selectedNotification.value = notification;
    showRejectModal.value = true;

    notificationsOpen.value = false;
};
const navigateTo = (route) => {
    if (route) {
        notificationsOpen.value = false;
        router.visit(route);
    }
};

const handleCtaNavigate = (notification, route) => {
    markNotificationRead(notification);
    if (notification.modal_type) {
        notificationsOpen.value = false;
        decisionsModalStore.openModal(notification.modal_type);
        return;
    }

    if (!route) {
        return;
    }

    // Click-tracking redirects (e.g. /go/{campaignKey}) and absolute URLs
    // need a full-page navigation so the server-side 302 from
    // CampaignRedirectController is followed by the browser. Inertia's
    // router.visit() uses XHR and can't follow cross-origin redirects, so
    // the click would otherwise silently fail to navigate.
    const needsFullPageNav =
        /^\/go\//.test(route) || /^https?:\/\//i.test(route);

    if (needsFullPageNav) {
        notificationsOpen.value = false;
        window.location.href = route;
        return;
    }

    navigateTo(route);
};

const goToAllNotifications = () => {
    notificationsOpen.value = false;
    router.visit('/notifications');
};

const redirectToInvitations = (notification) => {
    const link = notification.route;
    if (link && link.includes('settings/invitations')) {
        markNotificationRead(notification);
        window.location.href = link;
    }
};
const closeRejectModal = () => {
    showRejectModal.value = false;
};
const confirmRejectInvitation = () => {
    if (!selectedNotification.value || !selectedNotification.value.guid) {
        console.error('No invitation selected for rejection');
        return;
    }

    const guid = selectedNotification.value.guid;
    showRejectModal.value = false;
    notificationsOpen.value = false;
    loading.value = true;

    router.delete(`/settings/invitations/${guid}/delete`, {
        onSuccess: (page) => {
            const message =
                page.props.success || 'Invitation rejected successfully.';
            notify(
                {
                    group: 'success',
                    title: 'Success',
                    text: message,
                },
                4000
            );
            loading.value = false;
        },
        onError: (errors) => {
            notify(
                {
                    group: 'error',
                    title: 'Error',
                    text: 'Failed to reject invitation.',
                },
                4000
            );
            loading.value = false;
        },
        onFinish: () => {
            loading.value = false;
        },
    });
};

onMounted(() => {
    getNotifications();

    useEcho(
        `user.${usePage().props.userData.uuid}`,
        ['.app.notification', '.tracking.event'],
        (data) => {
            //console.log(data);
            data = data.data;
            if (data.type === 'generic-message') {
                getNotifications();
                notify(
                    {
                        group: data.group,
                        title: data.title,
                        text: data.message,
                    },
                    4000
                );
                ringThatBell();
            }

            if (data.type === 'sso-login') {
                window.gtag('config', 'G-0RRE7P1WE9');
                window.gtag('config', 'G-24V6GCLG87');

                window.gtag('event', 'My10x_Login', {
                    user_properties: {
                        joined_my10x: 'Yes',
                    },
                });
            }
            if (data.type === 'sso-register') {
                window.gtag('config', 'G-0RRE7P1WE9');
                window.gtag('config', 'G-24V6GCLG87');

                window.gtag('event', 'My10xRegistration', {
                    user_properties: {
                        joined_my10x: 'Yes',
                    },
                });
            }

            if (data.type === 'redemption-import') {
                getNotifications();
                notify(
                    {
                        group: 'success',
                        title: data.title || 'Redemption Imported',
                        text: data.message || '',
                        url: data.route || null,
                        urlLabel: 'View Redemption',
                    },
                    8000
                );
                ringThatBell();
            }

            if (data.type === 'redemption-import-failed') {
                getNotifications();
                notify(
                    {
                        group: 'error',
                        title: data.title || 'Import Failed',
                        text:
                            data.message ||
                            "We couldn't process your forwarded email.",
                    },
                    8000
                );
                ringThatBell();
            }

            if (data.type === 'failed-connection') {
                getNotifications();
                notify(
                    {
                        group: 'error',
                        title: 'Institution Connection Error',
                        text:
                            'There was an issue connecting to ' +
                            data.member.name +
                            '. <a href="/wallet/members">Please review the issue here.</a>',
                    },
                    30000
                );
                ringThatBell();
            }
        }
    ).listen();
});
</script>

<style scoped>
@keyframes bell-ring {
    0% {
        transform: rotate(0deg);
    }
    10% {
        transform: rotate(20deg);
    }
    20% {
        transform: rotate(-20deg);
    }
    30% {
        transform: rotate(15deg);
    }
    40% {
        transform: rotate(-15deg);
    }
    50% {
        transform: rotate(10deg);
    }
    60% {
        transform: rotate(-10deg);
    }
    70% {
        transform: rotate(5deg);
    }
    80% {
        transform: rotate(-5deg);
    }
    90% {
        transform: rotate(2deg);
    }
    100% {
        transform: rotate(0deg);
    }
}

.ring-bell {
    animation: bell-ring 0.8s ease-in-out;
    transform-origin: 50% 0;
}
</style>
